/** Default name for the first watchlist created for new accounts. */
export const DEFAULT_WATCHLIST_NAME = 'My Watchlist';

/** Default symbols seeded into the first watchlist. */
export const DEFAULT_WATCHLIST_TICKERS = [
  'SPY',
  'QQQ',
  'AAPL',
  'AMZN',
  'TSLA',
  'META',
  'GOOG',
  'NVDA',
  'BTCUSD',
];

/**
 * Premade list sources (client-side; not server watchlists).
 * Used by Relative Strength and the watchlist dropdown so every user can
 * scan popular groups without creating lists.
 * Keep each theme ~12–18 liquid names so live bar loads stay responsive
 * (see RELATIVE_STRENGTH_MAX_TICKERS). Combined AI may use the full RS cap.
 */
export const RS_SOURCE_WATCHLIST = 'watchlist';
export const RS_SOURCE_FAVORITES = 'favorites';
export const RS_USER_WATCHLIST_PREFIX = 'wl:';
/** Default RS table source for first visit when a broker is linked (classic mega-caps). */
export const RS_DEFAULT_TICKER_SOURCE = 'mag7';
/** Default RS table source when no broker is linked (AI software + hardware leaders). */
export const RS_UNLINKED_DEFAULT_TICKER_SOURCE = 'ai';
export const RS_AI_PRESET_TICKER_SOURCE = 'ai';

/**
 * First-visit Relative Strength list. Unlinked users land on the AI preset;
 * linked users keep Mag 7. A saved `pt:rsTickerSource` still wins.
 */
export function getDefaultRsTickerSource({ brokerLinked } = {}) {
  return brokerLinked ? RS_DEFAULT_TICKER_SOURCE : RS_UNLINKED_DEFAULT_TICKER_SOURCE;
}

export const PREMADE_RS_WATCHLISTS = [
  {
    id: 'mag7',
    name: 'Mag 7',
    tickers: ['AAPL', 'MSFT', 'GOOG', 'AMZN', 'META', 'NVDA', 'TSLA'],
  },
  {
    id: 'ai',
    name: 'AI',
    // Combined software + hardware leaders for the unlinked RS default.
    tickers: [
      'NVDA',
      'AVGO',
      'AMD',
      'TSM',
      'ASML',
      'MU',
      'AMAT',
      'LRCX',
      'KLAC',
      'ARM',
      'MRVL',
      'SMCI',
      'ANET',
      'MSFT',
      'GOOG',
      'META',
      'AMZN',
      'ORCL',
      'PLTR',
      'SNOW',
      'NOW',
      'CRM',
      'NET',
      'DDOG',
      'CRWD',
      'APP',
      'DELL',
      'VRT',
      'IBM',
      'CRWV',
    ],
  },
  {
    id: 'saas',
    name: 'SaaS',
    // Enterprise / cloud application software (excludes mega-caps in Mag 7)
    tickers: [
      'CRM', // Salesforce
      'NOW', // ServiceNow
      'ADBE', // Adobe
      'INTU', // Intuit
      'ORCL', // Oracle
      'WDAY', // Workday
      'SNOW', // Snowflake
      'DDOG', // Datadog
      'NET', // Cloudflare
      'TEAM', // Atlassian
      'MDB', // MongoDB
      'SHOP', // Shopify
      'ADSK', // Autodesk
      'VEEV', // Veeva
      'ZM', // Zoom
      'HUBS', // HubSpot
    ],
  },
  {
    id: 'ai-software',
    name: 'AI software',
    // Platforms, data, and apps monetizing AI (models + agents + infra software)
    tickers: [
      'MSFT',
      'GOOG',
      'META',
      'AMZN',
      'ORCL',
      'PLTR', // Palantir
      'SNOW',
      'NOW',
      'CRM',
      'NET',
      'DDOG',
      'APP', // AppLovin
      'IBM',
      'PATH', // UiPath
      'AI', // C3.ai
      'CRWD', // CrowdStrike (security + AI)
    ],
  },
  {
    id: 'ai-hardware',
    name: 'AI hardware',
    // GPUs, custom silicon, foundry/equipment, and AI server / networking stack
    tickers: [
      'NVDA',
      'AVGO', // Broadcom (custom ASICs + networking)
      'AMD',
      'TSM', // TSMC
      'ASML',
      'MU', // Micron (HBM)
      'AMAT', // Applied Materials
      'LRCX', // Lam Research
      'KLAC', // KLA
      'ARM',
      'MRVL', // Marvell
      'SMCI', // Super Micro
      'ANET', // Arista
      'DELL',
      'VRT', // Vertiv (power/cooling)
      'CRWV', // CoreWeave
      'NBIS', // Nebius
    ],
  },
  {
    id: 'semiconductors',
    name: 'Semiconductors',
    // Broader chip complex beyond the pure AI-buildout basket
    tickers: [
      'NVDA',
      'AVGO',
      'TSM',
      'ASML',
      'AMD',
      'QCOM',
      'TXN',
      'INTC',
      'MU',
      'AMAT',
      'LRCX',
      'KLAC',
      'MRVL',
      'NXPI',
      'ADI',
      'ON',
      'MCHP',
      'ARM',
    ],
  },
  {
    id: 'energy',
    name: 'Energy',
    // Oil majors + midstream + AI/data-center power (nuclear/IPPs) + uranium
    tickers: [
      'XOM',
      'CVX',
      'COP',
      'EOG',
      'SLB',
      'OXY',
      'WMB',
      'KMI',
      'CEG', // Constellation (nuclear / hyperscaler PPAs)
      'VST', // Vistra
      'NEE', // NextEra
      'NRG',
      'CCJ', // Cameco (uranium)
      'GEV', // GE Vernova
      'ETN', // Eaton
      'VRT', // Vertiv
    ],
  },
  {
    id: 'cybersecurity',
    name: 'Cybersecurity',
    tickers: [
      'CRWD',
      'PANW',
      'FTNT',
      'ZS',
      'OKTA',
      'S', // SentinelOne
      'GEN',
      'RBRK', // Rubrik
      'CYBR', // CyberArk
      'CHKP',
      'QLYS',
      'TENB',
      'VRNS',
    ],
  },
  {
    id: 'crypto',
    name: 'Crypto',
    // Spot proxies + exchanges + treasury + miners (high retail/pro attention)
    tickers: [
      'BTCUSD',
      'ETHUSD',
      'IBIT', // iShares Bitcoin
      'FBTC', // Fidelity Bitcoin
      'ETHA', // iShares Ethereum
      'MSTR', // Strategy (BTC treasury)
      'COIN', // Coinbase
      'HOOD', // Robinhood
      'MARA',
      'RIOT',
      'CLSK', // CleanSpark
      'BITF', // Bitfarms
      'XYZ', // Block (Cash App crypto)
      'GLXY', // Galaxy Digital
    ],
  },
  {
    id: 'fintech',
    name: 'Fintech',
    tickers: [
      'V', // Visa
      'MA', // Mastercard
      'PYPL',
      'XYZ', // Block
      'COIN',
      'HOOD',
      'AFRM', // Affirm
      'SOFI',
      'FI', // Fiserv
      'GPN', // Global Payments
      'TOST', // Toast
      'UPST', // Upstart
      'NU', // Nu Holdings
      'SCHW', // Charles Schwab
    ],
  },
  {
    id: 'biotech',
    name: 'Biotech',
    // Large-cap biotech / pharma growth names pros watch vs XBI theme
    tickers: [
      'LLY',
      'NVO',
      'AMGN',
      'VRTX',
      'REGN',
      'GILD',
      'MRK',
      'ABBV',
      'BMY',
      'PFE',
      'MRNA',
      'BNTX',
      'ISRG', // Intuitive Surgical (medtech adjacent)
      'DXCM',
      'ALNY',
    ],
  },
  // Major GICS sectors — liquid large-cap leaders (Energy is covered above).
  {
    id: 'sector-technology',
    name: 'Technology',
    tickers: [
      'AAPL',
      'MSFT',
      'NVDA',
      'AVGO',
      'ORCL',
      'CRM',
      'AMD',
      'ADBE',
      'CSCO',
      'ACN',
      'IBM',
      'INTC',
      'QCOM',
      'TXN',
      'NOW',
      'INTU',
    ],
  },
  {
    id: 'sector-financials',
    name: 'Financials',
    tickers: [
      'JPM',
      'BAC',
      'WFC',
      'GS',
      'MS',
      'C',
      'BLK',
      'SCHW',
      'BX',
      'SPGI',
      'AXP',
      'V',
      'MA',
      'USB',
      'PNC',
    ],
  },
  {
    id: 'sector-health-care',
    name: 'Health Care',
    tickers: [
      'LLY',
      'UNH',
      'JNJ',
      'ABBV',
      'MRK',
      'TMO',
      'ABT',
      'ISRG',
      'AMGN',
      'PFE',
      'DHR',
      'SYK',
      'BSX',
      'GILD',
      'MDT',
    ],
  },
  {
    id: 'sector-consumer-discretionary',
    name: 'Consumer Discretionary',
    tickers: [
      'AMZN',
      'TSLA',
      'HD',
      'MCD',
      'NKE',
      'LOW',
      'SBUX',
      'BKNG',
      'TJX',
      'CMG',
      'ORLY',
      'MAR',
      'GM',
      'F',
    ],
  },
  {
    id: 'sector-consumer-staples',
    name: 'Consumer Staples',
    tickers: [
      'WMT',
      'COST',
      'PG',
      'KO',
      'PEP',
      'PM',
      'MO',
      'MDLZ',
      'CL',
      'KMB',
      'GIS',
      'SYY',
      'KR',
      'TGT',
    ],
  },
  {
    id: 'sector-industrials',
    name: 'Industrials',
    tickers: [
      'GE',
      'CAT',
      'RTX',
      'HON',
      'UNP',
      'ETN',
      'UPS',
      'BA',
      'DE',
      'LMT',
      'ADP',
      'WM',
      'MMM',
      'GD',
      'PH',
    ],
  },
  {
    id: 'sector-materials',
    name: 'Materials',
    tickers: [
      'LIN',
      'SHW',
      'APD',
      'ECL',
      'FCX',
      'NEM',
      'CTVA',
      'DOW',
      'NUE',
      'VMC',
      'MLM',
      'DD',
      'ALB',
      'CF',
    ],
  },
  {
    id: 'sector-utilities',
    name: 'Utilities',
    tickers: [
      'NEE',
      'SO',
      'DUK',
      'CEG',
      'SRE',
      'AEP',
      'D',
      'EXC',
      'PEG',
      'XEL',
      'ED',
      'WEC',
      'PCG',
      'VST',
    ],
  },
  {
    id: 'sector-real-estate',
    name: 'Real Estate',
    tickers: [
      'PLD',
      'AMT',
      'EQIX',
      'WELL',
      'SPG',
      'O',
      'CCI',
      'DLR',
      'PSA',
      'VICI',
      'EXR',
      'AVB',
      'EQR',
      'CBRE',
    ],
  },
  {
    id: 'sector-communication',
    name: 'Communication Services',
    tickers: [
      'META',
      'GOOG',
      'NFLX',
      'DIS',
      'T',
      'VZ',
      'CMCSA',
      'TMUS',
      'CHTR',
      'EA',
      'TTWO',
      'WBD',
      'LYV',
    ],
  },
  {
    id: 'sector-etfs',
    name: 'Sector ETFs',
    // Select Sector SPDR lineup (classic broad sector coverage)
    tickers: [
      'XLK', // Technology
      'XLF', // Financials
      'XLE', // Energy
      'XLV', // Health Care
      'XLI', // Industrials
      'XLY', // Consumer Discretionary
      'XLP', // Consumer Staples
      'XLU', // Utilities
      'XLB', // Materials
      'XLRE', // Real Estate
      'XLC', // Communication Services
    ],
  },
  {
    id: 'classic-indices',
    name: 'Classic indices',
    tickers: ['SPY', 'QQQ', 'DIA', 'IWM', 'MDY', 'EFA', 'EEM', 'TLT', 'IAU', 'BTCUSD'],
  },
];

export function getPremadeRsWatchlist(id) {
  return PREMADE_RS_WATCHLISTS.find((w) => w.id === id) || null;
}

export function isPremadeWatchlistId(id) {
  return Boolean(getPremadeRsWatchlist(id));
}

export function isVirtualWatchlistId(id) {
  return String(id || '') === 'positions' || isPremadeWatchlistId(id);
}

/** Watchlist-row shape used by WatchlistsPanel / quote streams. */
export function premadeWatchlistTickers(id) {
  const list = getPremadeRsWatchlist(id);
  if (!list) return [];
  return list.tickers.map((ticker, index) => ({ ticker, position: index + 1 }));
}

export function rsUserWatchlistSourceId(watchlistId) {
  return `${RS_USER_WATCHLIST_PREFIX}${watchlistId}`;
}

export function parseRsUserWatchlistId(sourceId) {
  const raw = String(sourceId || '');
  if (!raw.startsWith(RS_USER_WATCHLIST_PREFIX)) return null;
  return raw.slice(RS_USER_WATCHLIST_PREFIX.length) || null;
}

export function normalizeTickerList(tickers = []) {
  return (tickers || [])
    .map((t) => String(t?.ticker || t?.Symbol || t || '').trim().toUpperCase())
    .filter(Boolean);
}

/**
 * Resolve RS comparison tickers for a source id.
 * - `favorites`: use `watchlistTickers` as the favorite symbols
 * - `watchlist` / unknown user list: use `watchlistTickers` arg
 * - premade id: return that list's tickers
 */
export function resolveRsTickerSource(sourceId, watchlistTickers = []) {
  if (sourceId === RS_SOURCE_FAVORITES) return normalizeTickerList(watchlistTickers);
  const premade = getPremadeRsWatchlist(sourceId);
  if (premade) return [...premade.tickers];
  return normalizeTickerList(watchlistTickers);
}

/** Grouped Lists menu: Favorites, user watchlists, then Popular premade lists. */
export function buildWatchlistSourceOptions(watchlists = []) {
  const opts = [
    { header: true, label: 'Favorites' },
    { value: RS_SOURCE_FAVORITES, label: 'Favorites' },
    { header: true, label: 'Watchlists' },
  ];
  if (watchlists?.length) {
    watchlists.forEach((watchlist) => {
      opts.push({
        value: rsUserWatchlistSourceId(watchlist.id),
        label: watchlist.name,
      });
    });
  } else {
    opts.push({ value: RS_SOURCE_WATCHLIST, label: 'My watchlist' });
  }
  opts.push({ header: true, label: 'Popular' });
  PREMADE_RS_WATCHLISTS.forEach((watchlist) => {
    opts.push({ value: watchlist.id, label: watchlist.name });
  });
  return opts;
}

export function watchlistSourceLabel(sourceId, watchlists = []) {
  if (sourceId === RS_SOURCE_FAVORITES) return 'Favorites';
  if (sourceId === 'custom') return 'Custom';
  if (sourceId === RS_SOURCE_WATCHLIST) {
    return watchlists.find((watchlist) => String(watchlist.id) !== 'positions')?.name || 'My watchlist';
  }
  const premade = getPremadeRsWatchlist(sourceId);
  if (premade) return premade.name;
  const userId = parseRsUserWatchlistId(sourceId);
  if (userId) {
    const match = watchlists.find((watchlist) => String(watchlist.id) === String(userId));
    if (match) return match.name;
  }
  return 'Lists';
}
